import { useState, useMemo } from "react";
import { Spin } from "antd";
import Detail from "../../../components/Details/Detail";
import ReferenceLinksDetailView from "../../../components/Details/ReferenceLinksDetailView";
import { useSelector } from "react-redux";
import {
  LABEL_TXT,
  ERROR_MSGS,
  TITLE_TXT,
  YESORNO,
  STATUS,
} from "../../../constants/constants";
import {
  capitalizeWords,
  formatCurrency,
  phoneNumberRules,
  websiteRules,
  descriptionRules,
  financialSummaryRules,
  emailRules,
} from "../../../helpers/helpers";
import dayjs from "dayjs";
import CustomModal from "../../../components/CustomModal/CustomModal";
import { FORM_MODAL_WIDTHS } from "../../../constants/constants";
import DetailSubHeader from "../../../components/Details/DetailSubHeader";
import ComAddressForm from "../../../components/CustomForms/ComAddressForm";
import { PERM_CODE } from "../../../constants/PermissionCodes";
import {
  isInvoiceStyleCategory,
  isVenturesCategory,
} from "../../../helpers/dealCategoryProducts";

const BusinessDetails = ({ businessDetailesData, orgId, status }) => {
  const { user } = useSelector((state) => state.auth);
  const { loading } = useSelector((state) => state?.business);
  const { dropdownData } = useSelector((state) => state?.enqMngt);
  const [modal, setModal] = useState({
    type: null, // "editComAdd"
    data: null, // hold address info
  });
  const userPrivileges = useMemo(() => {
    const raw = user?.privilege || [];
    return raw
      .map((p) => (typeof p === "string" ? p : p?.privilegeCode))
      .filter(Boolean);
  }, [user]);

  const rowStatus = businessDetailesData?.busDetails?.statusId || status;

  const editAccess =
    (userPrivileges.includes(PERM_CODE.BAEDT) &&
      rowStatus === STATUS.ACTIVE.ID) ||
    (userPrivileges.includes(PERM_CODE.BDEDT) &&
      rowStatus === STATUS.DEACTIVATED.ID) ||
    (userPrivileges.includes(PERM_CODE.BLEDT) &&
      rowStatus === STATUS.DELINQUENT.ID) ||
    (userPrivileges.includes(PERM_CODE.BBEDT) &&
      rowStatus === STATUS.BLACKLISTED.ID) ||
    (userPrivileges.includes(PERM_CODE.BIEDT) &&
      rowStatus === STATUS.INACTIVE.ID);

  const dealCategoryId = businessDetailesData?.busDetails?.dealCategoryId;

  const showCreditFields = isInvoiceStyleCategory(dealCategoryId);
  const showAmountToRaise = isVenturesCategory(dealCategoryId);
  const showIfScfNarrativeFields = showCreditFields;
  const isJointVenture = showAmountToRaise;

  const closeModal = () => setModal({ type: null, data: null });

  const handleComAddEdit = () => {
    const data = {
      orgId,
      dealCategoryId,
      billingAddLine1: businessDetailesData?.billingAdd?.address1,
      billingAddLine2: businessDetailesData?.billingAdd?.address2,
      billingCityName: businessDetailesData?.billingAdd?.city,
      billingStateName: businessDetailesData?.billingAdd?.state,
      billingZipCode: businessDetailesData?.billingAdd?.pinCode,
      modifiedAt: businessDetailesData?.modifiedAt,
    };
    setModal({ type: "editComAdd", data });
  };

  return (
    <div className="details-card-container">
      <div className="details-card">
        <DetailSubHeader title={TITLE_TXT.USER_DETS} />
        <Spin spinning={loading}>
          <div className="details-content">
            <Detail
              label={LABEL_TXT.POC_NAME}
              value={businessDetailesData?.userDetails?.contactName}
            />

            <Detail
              label={LABEL_TXT.POC_EMAIL}
              value={businessDetailesData?.userDetails?.email}
              name="email"
              placeholder="Enter POC Email"
              rules={emailRules("POC")}
              isVerified={
                businessDetailesData?.kycDetails?.kycEmail?.isVerified
              }
              isInput={true}
              orgId={orgId}
              dealCategoryId={dealCategoryId}
              isBus={true}
              modifiedAt={businessDetailesData?.modifiedAt}
            />

            <Detail
              label={LABEL_TXT.POC_PHONE}
              value={businessDetailesData?.userDetails?.phoneNumber}
              name="phoneNumber"
              placeholder="Enter Phone Number"
              rules={phoneNumberRules("POC")}
              isVerified={
                businessDetailesData?.kycDetails?.kycMobile?.isVerified
              }
              isInput={true}
              orgId={orgId}
              dealCategoryId={dealCategoryId}
              isBus={true}
              modifiedAt={businessDetailesData?.modifiedAt}
            />

            <Detail
              label={LABEL_TXT.POC_DESIG}
              value={businessDetailesData?.userDetails?.designation}
            />
          </div>

          <div className="details-title">
            <DetailSubHeader
              title={TITLE_TXT.COMM_ADD}
              hasEdit={editAccess}
              handleEdit={handleComAddEdit}
            />
          </div>

          <div className="details-content">
            <Detail
              label={LABEL_TXT.ADD1}
              value={businessDetailesData?.billingAdd?.address1}
            />
            <Detail
              label={LABEL_TXT.ADD2}
              value={businessDetailesData?.billingAdd?.address2}
            />
            <Detail
              label={LABEL_TXT.CITY}
              value={businessDetailesData?.billingAdd?.city}
            />
            <Detail
              label={LABEL_TXT.PIN}
              value={businessDetailesData?.billingAdd?.pinCode}
            />
            <Detail
              label={LABEL_TXT.STATE}
              value={businessDetailesData?.billingAdd?.state}
            />
          </div>

          <DetailSubHeader title={TITLE_TXT.BUS_DETS} />

          <div className="details-content">
            <Detail
              label={LABEL_TXT.BUS_NAME}
              value={businessDetailesData?.busDetails?.busName}
              isVerified={
                businessDetailesData?.kycDetails?.kycPanValue?.isVerified
              }
            />
            <Detail
              label={LABEL_TXT.BUS_WEB}
              value={businessDetailesData?.busDetails?.website?.replace(
                /^https?:\/\//i,
                "",
              )}
              name="website"
              placeholder="Enter Business Website"
              rules={websiteRules}
              isEditable={editAccess}
              isInput={true}
              orgId={orgId}
              dealCategoryId={dealCategoryId}
              isBus={true}
              modifiedAt={businessDetailesData?.modifiedAt}
            />
            <Detail
              label={LABEL_TXT.IND_TYPE}
              value={businessDetailesData?.busDetails?.industryTypeId}
              displayValue={businessDetailesData?.busDetails?.industryTypeName}
              name="industryTypeId"
              placeholder="Select Industry Type"
              options={dropdownData?.industryTypeList?.map((industry) => ({
                value: industry.industryTypeId,
                label: capitalizeWords(industry.industryTypeName),
              }))}
              rules={[{ required: true, message: ERROR_MSGS.SEL_IND_TYPE }]}
              isEditable={editAccess}
              orgId={orgId}
              dealCategoryId={dealCategoryId}
              isBus={true}
              modifiedAt={businessDetailesData?.modifiedAt}
            />
            <Detail
              label={LABEL_TXT.BUS_TYPE}
              value={businessDetailesData?.busDetails?.businessTypeName}
            />
            <Detail
              label={LABEL_TXT.ANN_TURN}
              value={businessDetailesData?.busDetails?.annualTurnoverId}
              displayValue={
                businessDetailesData?.busDetails?.annualTurnoverName
              }
              name="annualTurnoverId"
              placeholder="Select Annual Turnover"
              options={dropdownData?.annualTurnoverList?.map((item) => ({
                value: item.annualTurnoverId,
                label: capitalizeWords(item.annualTurnoverName),
              }))}
              rules={[{ required: true, message: ERROR_MSGS.SEL_ANN_TURN }]}
              isEditable={editAccess}
              orgId={orgId}
              dealCategoryId={dealCategoryId}
              isBus={true}
              modifiedAt={businessDetailesData?.modifiedAt}
            />
            {showCreditFields && (
              <>
                <Detail
                  label={LABEL_TXT.CRED_TEN}
                  value={businessDetailesData?.busDetails?.creditTenure}
                />
                <Detail
                  label={LABEL_TXT.REQ_CRED_AMT}
                  value={formatCurrency(
                    businessDetailesData?.busDetails?.requestedCreditAmt,
                  )}
                />
              </>
            )}
            {showAmountToRaise && (
              <Detail
                label={LABEL_TXT.AMT_TO_RAISE}
                value={formatCurrency(
                  businessDetailesData?.busDetails?.requestedCreditAmt,
                )}
              />
            )}
            <Detail
              label={LABEL_TXT.INC_DATE}
              value={dayjs(
                businessDetailesData?.busDetails?.incorporationDate,
              ).format("DD/MM/YYYY")}
            />
            <Detail
              label={LABEL_TXT.HAS_MUL_GST}
              value={businessDetailesData?.busDetails?.hasMultipleGstin}
              displayValue={
                businessDetailesData?.busDetails?.hasMultipleGstin
                  ? "Yes"
                  : "No"
              }
              name="hasMultipleGstin"
              options={YESORNO?.map((item) => ({
                value: item.value,
                label: item.label,
              }))}
              isEditable={editAccess}
              orgId={orgId}
              dealCategoryId={dealCategoryId}
              isBus={true}
              modifiedAt={businessDetailesData?.modifiedAt}
            />
            {showIfScfNarrativeFields && (
              <>
                <Detail
                  label={LABEL_TXT.BUS_DESC}
                  value={businessDetailesData?.busDetails?.busDescription}
                  name="description"
                  placeholder="Enter Business Description"
                  rules={descriptionRules("Business")}
                  isEditable={editAccess}
                  isInput={true}
                  isTextArea={true}
                  orgId={orgId}
                  dealCategoryId={dealCategoryId}
                  isBus={true}
                  modifiedAt={businessDetailesData?.modifiedAt}
                />
                <Detail
                  label={LABEL_TXT.FIN_SUM}
                  value={businessDetailesData?.busDetails?.financialSummary}
                  name="financialSummary"
                  placeholder="Enter Financial Summary"
                  rules={financialSummaryRules}
                  isEditable={editAccess}
                  isInput={true}
                  isTextArea={true}
                  orgId={orgId}
                  dealCategoryId={dealCategoryId}
                  isBus={true}
                  modifiedAt={businessDetailesData?.modifiedAt}
                />
              </>
            )}

            {/* Joint Venture Fields */}

            {isJointVenture && (
              <>
                <Detail
                  label="Others Terms & Conditions"
                  value={businessDetailesData?.busDetails?.otherTermsConditions}
                />
                <Detail
                  label="Deal Details"
                  value={businessDetailesData?.busDetails?.dealDetails}
                />
                <ReferenceLinksDetailView
                  referenceLinks={
                    businessDetailesData?.busDetails?.referenceLinks
                  }
                />
                <Detail
                  label="Deal Upload Document"
                  value={
                    businessDetailesData?.busDetails?.pitchDeck ? (
                      <a
                        href={businessDetailesData.busDetails.pitchDeck}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View Document
                      </a>
                    ) : (
                      "-"
                    )
                  }
                />
              </>
            )}
          </div>

          <div className="details-title">
            <DetailSubHeader title={TITLE_TXT.PRIM_ADD} />
          </div>

          <div className="details-content">
            <Detail
              label={LABEL_TXT.ADD1}
              value={businessDetailesData?.primaryAdd?.address1}
              isVerified={
                businessDetailesData?.kycDetails?.kycGstinValue?.isVerified
              }
            />
            <Detail
              label={LABEL_TXT.ADD2}
              value={businessDetailesData?.primaryAdd?.address2}
            />
            <Detail
              label={LABEL_TXT.CITY}
              value={businessDetailesData?.primaryAdd?.city}
            />
            <Detail
              label={LABEL_TXT.PIN}
              value={businessDetailesData?.primaryAdd?.pinCode}
            />
            <Detail
              label={LABEL_TXT.STATE}
              value={businessDetailesData?.primaryAdd?.state}
            />
          </div>

          {modal.type && (
            <CustomModal
              title={TITLE_TXT.EDIT_COM_ADD}
              open
              centered
              width={FORM_MODAL_WIDTHS}
              content={
                <ComAddressForm
                  data={modal.data}
                  onClose={closeModal}
                  isBus={true}
                  modifiedAt={businessDetailesData?.modifiedAt}
                />
              }
            />
          )}
        </Spin>
      </div>
    </div>
  );
};

export default BusinessDetails;
